import { useState, useEffect } from 'react';
import { getWeek } from '../components/performance-components/week_number.js';
import { 
  ANALYTICS_DEFAULT_VALUES, 
  getAnalyticsRightLayer 
} from '../config/analytics';
import { 
  formatAnalyticsDate, 
  calculateSliderMinDate, 
  modifiedIssueTimeFRDT,
  handleAnalyticsSliderTimeChange 
} from '../utils/analyticsDateUtils';
import { getDateRange } from '../utils/dateUtils';

/**
 * Custom hook for managing analytics view state
 * @returns {Object} State values and handlers
 */
export const useAnalyticsState = () => {
  // Core state
  const [cqlSevere, setCqlSevere] = useState(ANALYTICS_DEFAULT_VALUES.CQL_SEVERE);
  const [cqlFecha, setCqlFecha] = useState(ANALYTICS_DEFAULT_VALUES.CQL_FECHA);
  const [mapLeftLayer, setMapLeftLayer] = useState(ANALYTICS_DEFAULT_VALUES.MAP_LEFT_LAYER);
  const [mapRightLayer, setMapRightLayer] = useState(null);
  const [mapCrossborderLayer, setMapCrossborderLayer] = useState(ANALYTICS_DEFAULT_VALUES.MAP_CROSSBORDER_LAYER);
  
  // Button states
  const [selectedModel, setSelectedModel] = useState(ANALYTICS_DEFAULT_VALUES.SELECTED_MODEL);
  const [selectedRisk, setSelectedRisk] = useState(ANALYTICS_DEFAULT_VALUES.SELECTED_RISK);
  const [issueTime, setIssueTime] = useState('');
  const [issueTimeFRDT, setIssueTimeFRDT] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [issueTimeOptions, setIssueTimeOptions] = useState([]);
  
  // Crossborder states
  const [crossSelectedDate, setCrossSelectedDate] = useState('');
  const [crossSelectedTime, setCrossSelectedTime] = useState('');
  const [crossIssueTime, setCrossIssueTime] = useState([]);
  
  // UI state
  const [tabIndex, setTabIndex] = useState(ANALYTICS_DEFAULT_VALUES.TAB_INDEX);
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Computed values
  const { min: cqlFechaMin, max: cqlFechaMax } = getDateRange(cqlFecha);
  const datePart = cqlFecha.split(' ')[0];
  const weekNumber = getWeek(new Date(cqlFecha));
  const fechaMinSlider = calculateSliderMinDate(cqlFechaMin);

  // Update right layer when dependencies change
  useEffect(() => {
    const rightLayer = getAnalyticsRightLayer(mapLeftLayer, cqlSevere);
    setMapRightLayer(rightLayer);
  }, [mapLeftLayer, cqlSevere, weekNumber]);

  // Combine date and time for issue time and adjust FRDT
  useEffect(() => {
    if (selectedDate && selectedTime) {
      const adjustedFRDT = modifiedIssueTimeFRDT(selectedDate, selectedTime);
      setIssueTimeFRDT(adjustedFRDT);
      setIssueTime(`${selectedDate} ${selectedTime}`);
    }
  }, [selectedDate, selectedTime]);

  // Combine crossborder date and time
  useEffect(() => {
    if (crossSelectedDate && crossSelectedTime) {
      setCrossIssueTime([`${crossSelectedDate} ${crossSelectedTime}`]);
    }
  }, [crossSelectedDate, crossSelectedTime]);

  // Event handlers
  const handleDateChange = (fecha) => {
    const formattedDate = formatAnalyticsDate(fecha);
    setCqlFecha(formattedDate);
  };

  const handleModelClick = (value) => {
    setSelectedModel(value);
    setMapLeftLayer(value);
  };

  const handleSliderTimeChange = (nuevaFechaSlider) => {
    const nuevaFecha = handleAnalyticsSliderTimeChange(nuevaFechaSlider, datePart);
    setCqlFecha(nuevaFecha);
  };

  const handleRiskClick = (value) => {
    setSelectedRisk(value);
    setCqlSevere(value);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleCrossDateSelect = (date) => {
    setCrossSelectedDate(date);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleCrossTimeSelect = (time) => {
    setCrossSelectedTime(time);
  };

  const handleFilterChange = (event, value) => {
    setCqlSevere(value || 'Light');
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleBackdropOpen = () => {
    setBackdropOpen(true);
  };

  const handleBackdropClose = () => {
    setBackdropOpen(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return {
    // State values
    cqlSevere,
    cqlFecha,
    cqlFechaMin,
    cqlFechaMax,
    datePart,
    weekNumber,
    fechaMinSlider,
    mapLeftLayer,
    mapRightLayer,
    mapCrossborderLayer,
    selectedModel,
    selectedRisk,
    issueTime,
    issueTimeFRDT,
    selectedDate,
    selectedTime,
    crossSelectedDate,
    crossSelectedTime,
    crossIssueTime,
    issueTimeOptions,
    tabIndex,
    backdropOpen,
    anchorEl,
    
    // State setters
    setCqlSevere,
    setCqlFecha,
    setSelectedModel,
    setSelectedRisk,
    setIssueTime,
    setIssueTimeFRDT,
    setSelectedDate,
    setSelectedTime,
    setCrossSelectedDate,
    setCrossSelectedTime,
    setCrossIssueTime,
    setIssueTimeOptions,
    setTabIndex,
    setBackdropOpen,
    setAnchorEl,
    
    // Event handlers
    handleDateChange,
    handleModelClick,
    handleSliderTimeChange,
    handleRiskClick,
    handleDateSelect,
    handleCrossDateSelect,
    handleTimeSelect,
    handleCrossTimeSelect,
    handleFilterChange,
    handleTabChange,
    handleBackdropOpen,
    handleBackdropClose,
    handleClose,
  };
};
